import { useState, useRef, useEffect } from 'react'
import { Search, MapPin } from 'lucide-react'
import { SidebarInput } from '@/components/ui/shadcn/sidebar'
import { VisitorListItem } from './VisitorListItem'
import type { Visitor, PresenceStats } from './types'

interface VisitorListProps {
  visitors: Visitor[]
  selectedId: string | null
  onSelect: (visitor: Visitor) => void
  onHover?: (visitor: Visitor) => void
  onHoverEnd?: () => void
  onStartConversation?: (visitor: Visitor) => void
  presenceStats: PresenceStats
}

export function VisitorList({ visitors, selectedId, onSelect, onHover, onHoverEnd, onStartConversation, presenceStats }: VisitorListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll selected visitor into view when selectedId changes
  useEffect(() => {
    if (!selectedId || !listRef.current) return

    const element = listRef.current.querySelector(`[data-visitor-id="${selectedId}"]`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedId])

  const filteredVisitors = searchQuery
    ? visitors.filter(v =>
        v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.location?.city?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : visitors

  return (
    <div className="hidden md:flex w-80 flex-col min-h-0 border-r border-sidebar-border bg-sidebar-secondary text-sidebar-secondary-foreground">
      {/* Header */}
      <div className="gap-3.5 border-b border-sidebar-secondary-accent/30 p-4">
        <div className="flex w-full items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="size-4 text-primary" />
            <span className="font-medium">Visitors</span>
          </div>
          <div className="text-xs text-sidebar-secondary-muted">
            {presenceStats.live_count} live / {presenceStats.active_count} active
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-sidebar-secondary-muted" />
          <SidebarInput
            placeholder="Search visitors..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {filteredVisitors.length === 0 ? (
          <EmptyState searchQuery={searchQuery} />
        ) : (
          filteredVisitors.map((visitor) => (
            <VisitorListItem
              key={visitor.id}
              visitor={visitor}
              isSelected={selectedId === visitor.id}
              onClick={() => onSelect(visitor)}
              onHover={() => onHover?.(visitor)}
              onHoverEnd={onHoverEnd}
              onStartConversation={() => onStartConversation?.(visitor)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function EmptyState({ searchQuery }: { searchQuery: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center px-4">
      <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center mb-3">
        <MapPin className="size-6 text-primary" />
      </div>
      <p className="text-sm text-sidebar-secondary-muted">
        {searchQuery ? "No visitors found" : "No visitors with locations yet"}
      </p>
    </div>
  )
}
